import { Box, Table, Text, Center, Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { CompareProductSource } from '@/utils/compareMetadata';
import { buildMetadataCompareRows } from '@/utils/compareMetadata';
import classes from './CompareMetadataTable.module.css';

type CompareMetadataTableProps = {
  products: CompareProductSource[];
  accentColor: string;
  loading?: boolean;
};

export function CompareMetadataTable({
  products,
  accentColor: _accentColor,
  loading = false,
}: CompareMetadataTableProps) {
  const { t } = useTranslation();
  const rows = buildMetadataCompareRows(products);

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" color={_accentColor} />
      </Center>
    );
  }

  if (products.length < 2) {
    return (
      <Box className={classes.wrap}>
        <Text c="dimmed" size="sm">
          {t('marketplace.compare.metaNeedTwo')}
        </Text>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box className={classes.wrap}>
        <Text c="dimmed" size="sm">
          {t('marketplace.compare.metaNone')}
        </Text>
      </Box>
    );
  }

  return (
    <Box className={classes.wrap}>
      <div className={classes.scroll}>
        <Table
          className={classes.table}
          striped
          highlightOnHover
          withTableBorder
          withColumnBorders
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.keyCol} />
              {products.map((product) => (
                <Table.Th key={product._id} className={classes.valueCell}>
                  <Text size="sm" fw={600} lineClamp={2} className={classes.valueText}>
                    {product.productName}
                  </Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr
                key={row.key}
                className={
                  row.status === 'different'
                    ? classes.rowDifferent
                    : row.status === 'same'
                      ? classes.rowSame
                      : undefined
                }
              >
                <Table.Td className={classes.keyCol}>
                  <Text fw={600} size="sm">
                    {row.label}
                  </Text>
                </Table.Td>
                {row.values.map((value, index) => (
                  <Table.Td
                    key={`${row.key}-${products[index]?._id ?? index}`}
                    className={classes.valueCell}
                  >
                    <Text
                      size="sm"
                      c={value ? undefined : 'dimmed'}
                      className={classes.valueText}
                      title={value ?? undefined}
                    >
                      {value ?? '—'}
                    </Text>
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>
    </Box>
  );
}
